// src/services/checkInConfigApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { 
  OMNISTACK_BASE_URL, 
  getPrepareHeaders 
} from '@/hooks/common/common-functions'

// Types
export interface CheckInConfiguration {
  enabled: boolean
  defaultJobSiteRadius: number
  autoCheckInTimeout: number
  allowFallbackWithoutLocation: boolean
  requireLocationOverride: boolean
  roleSettings: RoleCheckInSettings[]
  siteOverrides: SiteOverrideSettings[]
  allowManualLocationEntry: boolean
  requireReasonForOverride: boolean
  logAllAttempts: boolean
  enableOfflineMode: boolean
  syncFrequencyMinutes: number
  enableLocationHistory: boolean
  maxLocationHistoryDays: number
}

export interface RoleCheckInSettings {
  roleName: string
  methods: {
    appButton: CheckInRequirement
    qrCode: CheckInRequirement
    nfcTap: CheckInRequirement
    autoGeofence: CheckInRequirement
  }
  locationRequirement: LocationRequirement
  primaryMethod: CheckInMethod
  terminology: UserTerminology
  enhancedVerification: boolean
  allowOverride: boolean
}

export interface SiteOverrideSettings {
  siteId: string
  siteName: string
  overrides: {
    forceMethod?: string
    locationAccuracy?: number
    requirePhoto?: boolean
    requireSignature?: boolean
    customRadius?: number
  }
}

export enum CheckInRequirement {
  REQUIRE = 'require',
  ALLOW = 'allow',
  DISABLE = 'disable'
}

export enum LocationRequirement {
  ALWAYS = 'always',
  OPTIONAL = 'optional',
  NEVER = 'never'
}

export enum CheckInMethod {
  APP_BUTTON = 'app_button',
  QR_CODE = 'qr_code',
  NFC_TAP = 'nfc_tap',
  AUTO_GEOFENCE = 'auto_geofence'
}

export enum UserTerminology {
  CLOCK_IN = 'clock_in',
  CHECK_IN = 'check_in',
  ARRIVE = 'arrive',
  START_SHIFT = 'start_shift'
}

export interface UpdateCheckInConfigRequest {
  businessId: string
  enabled: boolean
  defaultJobSiteRadius: number
  autoCheckInTimeout: number
  allowFallbackWithoutLocation: boolean
  requireLocationOverride: boolean
  roleSettings: RoleCheckInSettings[]
  siteOverrides: SiteOverrideSettings[]
  allowManualLocationEntry: boolean
  requireReasonForOverride: boolean
  logAllAttempts: boolean
  enableOfflineMode: boolean
  syncFrequencyMinutes: number
  enableLocationHistory: boolean
  maxLocationHistoryDays: number
}

export interface UpdateRoleSettingsRequest {
  businessId: string
  roleName: string
  methods: {
    appButton: CheckInRequirement
    qrCode: CheckInRequirement
    nfcTap: CheckInRequirement
    autoGeofence: CheckInRequirement
  }
  locationRequirement: LocationRequirement
  primaryMethod: CheckInMethod
  terminology: UserTerminology
  enhancedVerification: boolean
  allowOverride: boolean
}

export interface CreateSiteOverrideRequest {
  businessId: string
  siteId: string
  siteName: string
  overrides: {
    forceMethod?: string
    locationAccuracy?: number
    requirePhoto?: boolean
    requireSignature?: boolean
    customRadius?: number
  }
}

export const checkInConfigApi = createApi({
  reducerPath: 'checkInConfigApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${OMNISTACK_BASE_URL}business/checkin-config`,
    prepareHeaders: (headers) => {
      // Add standard headers
      getPrepareHeaders({
        headers,
        isClientApiKey: true,
      });
      
      // Add business-specific API key
      const businessApiKey = localStorage.getItem('businessApiKey');
      if (businessApiKey) {
        headers.set('business-x-api-key', businessApiKey);
      }
      
      return headers;
    },
  }),
  tagTypes: ['CheckInConfig', 'RoleSettings', 'SiteOverrides'],
  endpoints: (builder) => ({
    // ============================================================================
    // CONFIGURATION ENDPOINTS
    // ============================================================================
    
    // Get check-in configuration
    getCheckInConfig: builder.query<CheckInConfiguration, string>({
      query: (businessId) => ({
        url: '',
        params: { businessId }
      }),
      transformResponse: (response: any) => response.data?.config || response,
      providesTags: ['CheckInConfig']
    }),
    
    // Update check-in configuration
    updateCheckInConfig: builder.mutation<any, UpdateCheckInConfigRequest>({
      query: ({ businessId, ...configData }) => ({
        url: '',
        method: 'PUT',
        params: { businessId },
        body: configData
      }),
      invalidatesTags: ['CheckInConfig', 'RoleSettings', 'SiteOverrides']
    }),

    // ============================================================================
    // ROLE SETTINGS ENDPOINTS
    // ============================================================================
    
    // Update role-specific settings
    updateRoleSettings: builder.mutation<any, UpdateRoleSettingsRequest>({
      query: ({ businessId, ...roleData }) => ({
        url: '/role-settings',
        method: 'PUT',
        params: { businessId },
        body: roleData
      }),
      invalidatesTags: ['CheckInConfig', 'RoleSettings']
    }),

    // ============================================================================
    // SITE OVERRIDE ENDPOINTS
    // ============================================================================
    
    // Create site-specific override
    createSiteOverride: builder.mutation<any, CreateSiteOverrideRequest>({
      query: ({ businessId, ...siteData }) => ({
        url: '/site-override',
        method: 'POST',
        params: { businessId },
        body: siteData
      }),
      invalidatesTags: ['CheckInConfig', 'SiteOverrides']
    }),
    
    // Delete site override
    deleteSiteOverride: builder.mutation<any, { businessId: string; siteId: string }>({
      query: ({ businessId, siteId }) => ({
        url: `/site-override/${siteId}`,
        method: 'DELETE',
        params: { businessId }
      }),
      invalidatesTags: ['CheckInConfig', 'SiteOverrides']
    }),

    // ============================================================================
    // UTILITY ENDPOINTS
    // ============================================================================
    
    // Get default configuration template
    getDefaultConfig: builder.query<CheckInConfiguration, string>({
      query: (businessId) => ({
        url: '/defaults',
        params: { businessId }
      }),
      providesTags: ['CheckInConfig']
    })
  })
});

export const {
  // Configuration hooks
  useGetCheckInConfigQuery,
  useUpdateCheckInConfigMutation,
  
  // Role settings hooks
  useUpdateRoleSettingsMutation,
  
  // Site override hooks
  useCreateSiteOverrideMutation,
  useDeleteSiteOverrideMutation,
  
  // Utility hooks
  useGetDefaultConfigQuery
} = checkInConfigApi;